import RouteError from '@/components/ui/route-error';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DICTIONARY } from '@/config/dictionary';
import { queryClient } from '@/lib/query-client';
import { requireAuth } from '@/utils/require-auth-before-load';
import { APP_PATHS } from '@/config/routes/app-paths';
import { ensureValidPath } from '@/utils/validate-path';
import { Input } from '@/components/ui/lib/input';
import { Box } from '@/components/ui/lib/box';
import { Text } from '@/components/ui/lib/text';
import { Checkbox } from '@/components/ui/lib/checkbox';
import { Separator } from '@/components/ui/lib/separator';
import FormField from '@/components/ui/form-field';
import CatalogItemEditorShell from '@/features/admin-settings/components/catalog-item-editor-shell';
import {
  getRolesQueryOptions,
  getPermissionsQueryOptions,
  useRoles,
  usePermissions,
  useCreateRole,
  useUpdateRole,
  useSetRolePermissions,
  RoleAdminSchema,
} from '@/features/admin-settings/api/rbac-api';

const RoleEditor = () => {
  const navigate = useNavigate();
  const { roleId } = Route.useParams();
  const isCreate = roleId === 'new';

  const d = DICTIONARY.adminSettingsPage.rolesAdmin.editor;

  const { data: roles, isLoading, refetch: refetchRoles } = useRoles();
  const { data: permissions } = usePermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const setRolePermissions = useSetRolePermissions();

  const existingRole = useMemo(() => roles?.find((r) => r.id === roleId), [roles, roleId]);

  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>(
    existingRole?.permissions ?? [],
  );

  // `roles` is prefetched by the route loader and is normally already resolved on
  // first render, but this guards the case where `existingRole` only becomes
  // available after mount (e.g. a slow query) — mirrors the ref-guard pattern in
  // src/hooks/use-bootstrap-auth.ts rather than resyncing on every render.
  const hasSyncedPermissions = useRef(false);
  useEffect(() => {
    if (hasSyncedPermissions.current || existingRole == null) return;
    hasSyncedPermissions.current = true;
    setSelectedPermissionIds(existingRole.permissions);
  }, [existingRole]);

  const isSaving = createRole.isPending || updateRole.isPending || setRolePermissions.isPending;

  const goBack = () => {
    void navigate({ to: ensureValidPath(APP_PATHS.adminSettings) });
  };

  const form = useForm({
    defaultValues: {
      type: existingRole?.type ?? '',
      scope: existingRole?.scope ?? '',
    },
    onSubmit: async ({ value }) => {
      const payload = RoleAdminSchema.parse(value);
      const savedRole = isCreate
        ? await createRole.mutateAsync(payload)
        : await updateRole.mutateAsync({ id: roleId, payload });
      await setRolePermissions.mutateAsync({
        id: isCreate ? savedRole.id : roleId,
        payload: { permissionIds: selectedPermissionIds },
      });
      goBack();
    },
  });

  const togglePermission = (permissionId: string) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  return (
    <CatalogItemEditorShell
      isLoading={isLoading}
      loadFailed={!isLoading && roles == null}
      onRetry={() => {
        void refetchRoles();
      }}
      notFound={!isCreate && roles != null && existingRole == null}
      isCreate={isCreate}
      title={isCreate ? d.addTitle : d.editTitle}
      subtitle={isCreate ? d.addSubtitle : d.editSubtitle(existingRole?.type ?? '')}
      onBack={goBack}
      onCancel={goBack}
      onSave={() => {
        void form.handleSubmit();
      }}
      isSaving={isSaving}
      saveDisabled={isSaving}
      saveLabel={DICTIONARY.common.saveChanges}
      backLabel={d.backLabel}
      cancelLabel={DICTIONARY.common.cancel}
    >
      <Box display="flex" flexDirection="column" gap="4">
        <form.Field name="type" validators={{ onSubmit: RoleAdminSchema.shape.type }}>
          {(field) => (
            <FormField field={field} label={d.typeLabel} required>
              <Input
                value={field.state.value}
                placeholder={d.typePlaceholder}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.handleChange(e.target.value);
                }}
                onBlur={field.handleBlur}
                size="sm"
              />
            </FormField>
          )}
        </form.Field>

        <form.Field name="scope" validators={{ onSubmit: RoleAdminSchema.shape.scope }}>
          {(field) => (
            <FormField field={field} label={d.scopeLabel} required>
              <Input
                value={field.state.value}
                placeholder={d.scopePlaceholder}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.handleChange(e.target.value);
                }}
                onBlur={field.handleBlur}
                size="sm"
              />
            </FormField>
          )}
        </form.Field>

        <Separator />

        <Box>
          <Text fontSize="sm" fontWeight="600" className="text-base" mb="2">
            {d.permissionsHeading}
          </Text>
          <Box display="flex" flexDirection="column" gap="2">
            {(permissions ?? []).map((permission) => (
              <Checkbox.Root
                key={permission.id}
                checked={selectedPermissionIds.includes(permission.id)}
                onCheckedChange={() => {
                  togglePermission(permission.id);
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label fontSize="sm">
                  {permission.name ?? permission.key ?? permission.id}
                </Checkbox.Label>
              </Checkbox.Root>
            ))}
          </Box>
        </Box>
      </Box>
    </CatalogItemEditorShell>
  );
};

export const Route = createFileRoute('/admin-settings/roles-admin/$roleId')({
  beforeLoad: requireAuth,
  loader: async () => {
    await Promise.allSettled([
      queryClient.prefetchQuery(getRolesQueryOptions()),
      queryClient.prefetchQuery(getPermissionsQueryOptions()),
    ]);
    return null;
  },
  component: RoleEditor,
  errorComponent: RouteError,
});
